import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { alanBicimi, HATA_BICIMI, YARDIM_BICIMI } from "./alan-bicimi";
import { AlanEtiketi } from "./AlanEtiketi";
import { cn } from "./cn";
import { Ikon } from "./Ikon";

export interface AlanProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  etiket: string;
  /**
   * Etiketi GORSEL olarak gizler — ekran okuyucuda kalir.
   *
   * `Secim` ve `MetinAlani` bunu zaten tasiyordu; `Alan` ailenin tek
   * eksigiydi ve bu eksik, bir tabloda ya da satir icinde yan yana
   * duran alanlarin `Alan` YERINE ELLE yazilmasina yol aciyordu —
   * yani tam da `form-bileseni.test.ts`in engellemeye calistigi sey.
   * (06.09.2026, mesai saati satirlari.)
   *
   * Etiketi tamamen kaldirmak alani ekran okuyucuda ISIMSIZ birakirdi;
   * o yuzden `etiket` yine ZORUNLU ve satiri tanimlayacak kadar acik
   * olmali ("Pazartesi baslangic", "Islem" degil).
   */
  etiketGizli?: boolean;
  /**
   * Alan bos birakilamaz: etiketin yaninda "(zorunlu)" + `aria-required`.
   * Yalnizca GERCEK dogrulama kurali olan alana verilir; arama/suzgec
   * kutularina topluca eklemek isareti anlamsizlastirir. Yerli
   * `required` KOYMUYOR — formlar kendi dogrulamasini yapiyor ve
   * tarayici balonu onun mesajinin onune gecerdi.
   */
  zorunlu?: boolean;
  hata?: string;
  yardim?: ReactNode;
}

/**
 * Etiketli metin alani.
 *
 * Etiket HER ZAMAN vardir ve `htmlFor` ile baglidir — yalnizca placeholder
 * kullanmak ekran okuyucuda alani isimsiz birakir ve kullanici yazmaya
 * basladiginda ipucu kaybolur.
 *
 * Hata mesaji `aria-describedby` ile alana baglanir ve `role="alert"` ile
 * duyurulur; kirmizi kenarlik tek basina yeterli degildir (renk korlugu).
 *
 * Alti gorsel hal `alan-bicimi.ts` icinde ve DORT form bileseni de ayni
 * dosyadan besleniyor — sozlesme orada, tekrar burada degil.
 */
export function Alan({
  etiket,
  etiketGizli,
  zorunlu = false,
  hata,
  yardim,
  className,
  readOnly,
  ...rest
}: AlanProps) {
  const id = useId();
  const hataId = `${id}-hata`;
  const yardimId = `${id}-yardim`;

  return (
    <div className="flex flex-col gap-1.5">
      <AlanEtiketi htmlFor={id} etiket={etiket} gizli={etiketGizli} zorunlu={zorunlu} />

      <input
        {...rest}
        id={id}
        readOnly={readOnly}
        aria-required={zorunlu || undefined}
        aria-invalid={hata ? true : undefined}
        aria-describedby={cn(hata && hataId, yardim && yardimId) || undefined}
        className={alanBicimi({
          hata: Boolean(hata),
          saltOkunur: Boolean(readOnly),
          dokunmaHedefi: true,
          ekSinif: className,
        })}
      />

      {yardim && (
        <p id={yardimId} className={YARDIM_BICIMI}>
          {yardim}
        </p>
      )}
      {hata && (
        <p id={hataId} role="alert" className={HATA_BICIMI}>
          {/* Ikon `aria-hidden`; anlami tasiyan sey metnin kendisi. */}
          <Ikon ad="hata" boyut="kucuk" className="mt-px" />
          {hata}
        </p>
      )}
    </div>
  );
}
